import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { BookingDetailsModal } from "./Bookings.jsx";
import { PageSkeleton } from "@daycal/ui";
import { api } from "../lib/apiClient.js";
import { colorOf, dayKeyOf, timeLabelOf } from "../lib/bookingColors.js";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MAX_CHIPS = 3; // events shown per day cell before "+N more"

// A month-grid calendar (Google-Calendar style) that shows every booking as a
// colour-coded event chip. Clicking any booking navigates to the Bookings page
// and highlights that booking there.
export default function BookingCalendar() {
  const [detailId, setDetailId] = useState(null);
  const { data, isLoading } = useQuery({
    queryKey: ["bookings"],
    queryFn: () => api.listBookings(),
  });

  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const bookings = data?.bookings || [];

  const byDay = useMemo(() => {
    const map = {};
    for (const b of bookings) (map[dayKeyOf(b)] ||= []).push(b);
    for (const k of Object.keys(map))
      map[k].sort((a, b) => a.start_utc.localeCompare(b.start_utc));
    return map;
  }, [bookings]);

  const openBooking = (b) => setDetailId(b.id);

  if (isLoading) return <PageSkeleton />;

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const monthName = cursor.toLocaleString("en-US", {
    month: "long",
    year: "numeric",
  });
  const todayKey = new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());

  // Build a 6-row (42-cell) grid starting from the Sunday on/before the 1st.
  const firstDow = new Date(year, month, 1).getDay();
  const gridStart = new Date(year, month, 1 - firstDow);
  const cells = Array.from({ length: 42 }, (_, i) => {
    const d = new Date(
      gridStart.getFullYear(),
      gridStart.getMonth(),
      gridStart.getDate() + i,
    );
    const key = new Intl.DateTimeFormat("en-CA", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(d);
    return {
      date: d,
      key,
      inMonth: d.getMonth() === month,
      list: byDay[key] || [],
    };
  });

  const total = bookings.filter((b) =>
    dayKeyOf(b).startsWith(`${year}-${String(month + 1).padStart(2, "0")}`),
  ).length;

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900">
            Booking Calendar
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Every booking at a glance — click one to open it in Bookings.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCursor(new Date(year, month - 1, 1))}
            aria-label="Previous month"
            className="grid h-10 w-10 place-items-center rounded-xl border border-slate-200/80 bg-white text-slate-500 shadow-sm transition-all hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900"
          >
            <FiChevronLeft className="h-4 w-4" />
          </button>
          <span className="font-display min-w-[11rem] text-center text-xl font-bold tracking-tight text-slate-900">
            {monthName}
          </span>
          <button
            onClick={() => setCursor(new Date(year, month + 1, 1))}
            aria-label="Next month"
            className="grid h-10 w-10 place-items-center rounded-xl border border-slate-200/80 bg-white text-slate-500 shadow-sm transition-all hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900"
          >
            <FiChevronRight className="h-4 w-4" />
          </button>
          <button
            onClick={() => {
              const d = new Date();
              setCursor(new Date(d.getFullYear(), d.getMonth(), 1));
            }}
            className="ml-2 rounded-xl border border-slate-200/80 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition-all hover:border-slate-300 hover:bg-slate-50"
          >
            Today
          </button>
        </div>
      </div>

      {/* Calendar grid */}
      <div className="overflow-hidden rounded-[1.5rem] border border-slate-200/60 bg-white shadow-2xl shadow-slate-200/40 ring-1 ring-slate-900/5">
        {/* Weekday header */}
        <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50/50">
          {WEEKDAYS.map((w) => (
            <div
              key={w}
              className="px-2 py-2 text-center text-[10px] font-bold uppercase tracking-[0.1em] text-slate-400"
            >
              <span className="hidden sm:inline">{w}</span>
              <span className="sm:hidden">{w[0]}</span>
            </div>
          ))}
        </div>

        {/* Day cells */}
        <div className="grid grid-cols-7 bg-slate-50/30">
          {cells.map((cell, i) => {
            const isToday = cell.key === todayKey;
            const extra = cell.list.length - MAX_CHIPS;
            return (
              <div
                key={cell.key}
                className={`group min-h-[90px] border-b border-r border-slate-200 p-1.5 sm:min-h-[105px] transition-colors hover:bg-slate-50/80 ${(i + 1) % 7 === 0 ? "border-r-0" : ""} ${
                  isToday
                    ? "bg-brand-50/30"
                    : cell.inMonth
                      ? "bg-white"
                      : "bg-transparent"
                }`}
              >
                {/* Day number */}
                <div className="mb-1 flex justify-end px-1">
                  <span
                    className={`flex h-7 w-7 items-center justify-center rounded-full text-[13px] font-semibold transition-colors ${
                      isToday
                        ? "bg-brand-500 text-white shadow-md shadow-brand-500/20 ring-4 ring-brand-50"
                        : cell.inMonth
                          ? "text-slate-700 group-hover:text-brand-600"
                          : "text-slate-400"
                    }`}
                  >
                    {cell.date.getDate()}
                  </span>
                </div>

                {/* Event chips */}
                <div className="space-y-1.5 px-0.5">
                  {cell.list.slice(0, MAX_CHIPS).map((b) => {
                    const c = colorOf(b.status);
                    return (
                      <button
                        key={b.id}
                        onClick={() => openBooking(b)}
                        title={`${b.invitee_name || "Booking"} · ${timeLabelOf(b)} · ${c.label}`}
                        className={`flex w-full items-center gap-1.5 overflow-hidden rounded-md border px-1.5 py-1.5 text-left text-[11px] font-medium leading-none transition-all hover:scale-[1.02] hover:shadow-sm ${c.chip}`}
                      >
                        <span
                          className={`h-2.5 w-1 shrink-0 rounded-full ${c.bar}`}
                        />
                        <span className="hidden shrink-0 tabular-nums opacity-80 sm:inline">
                          {timeLabelOf(b)}
                        </span>
                        <span className="truncate">
                          {b.invitee_name || "(anonymized)"}
                        </span>
                      </button>
                    );
                  })}
                  {extra > 0 && (
                    <button
                      onClick={() => openBooking(cell.list[MAX_CHIPS])}
                      className="mt-1 w-full rounded-md px-2 py-1 text-left text-[11px] font-semibold text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
                    >
                      +{extra} more
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer: legend + month total */}
      <div className="mt-6 flex flex-wrap items-center justify-between gap-4 px-2">
        <div className="flex flex-wrap gap-x-5 gap-y-2">
          {["confirmed", "completed", "hold", "cancelled"].map((s) => (
            <span
              key={s}
              className="flex items-center gap-2 text-[13px] font-medium text-slate-500"
            >
              <span
                className={`h-2.5 w-2.5 rounded-full ${colorOf(s).dot} shadow-sm`}
              />
              {colorOf(s).label}
            </span>
          ))}
        </div>
        <span className="text-[13px] font-medium text-slate-400">
          {total} booking{total === 1 ? "" : "s"} in {monthName}
        </span>
      </div>

      {detailId && <BookingDetailsModal bookingId={detailId} onClose={() => setDetailId(null)} />}
    </div>
  );
}
